import { createAsyncThunk } from "@reduxjs/toolkit";
import { SolarProduct } from "../../model/solar-product/solar-product";
import { PaginatedList } from "../../model/common/paginated-list";
import { RootState } from "../store";
import { ApplianceItem } from "../../model/solar-recommendation-form/appliance-item";
import { SolarRecommendationForm } from "../../model/solar-recommendation-form/solar-recommendation-form";
import * as service from "../../service/solar-product-service";

// Helper functions for calculations
function calculateTotalPower(appliances: ApplianceItem[]) {
  return appliances.reduce((acc, appliance) => acc + appliance.quantity * appliance.dailyUsage * appliance.appliance.power, 0);
}

function calculateTotalPowerNeeded(appliances: ApplianceItem[]) {
  const totalKwh = calculateTotalPower(appliances) / 1000;
  const longestTimeUsed = appliances.reduce((acc, appliance) => Math.max(acc, appliance.dailyUsage), 0);
  return totalKwh / longestTimeUsed;
}

function getMonthsToPay(form: SolarRecommendationForm) {
  return form.paymentPlan?.paymentPlan ?? 0;
}

function getMonthlyBudget(form: SolarRecommendationForm) {
  return form.paymentPlan?.solarMonthlyRepayment ?? 0;
}

// Updated thunk that uses the endpoints from the service file
export const recommendProducts = createAsyncThunk<{
  bestFit: SolarProduct[],
  budgetFit: PaginatedList<SolarProduct>,
  powerNeedsFit: PaginatedList<SolarProduct>
}, { page: number, pageSize: number }, { state: RootState }>(
  'solarRecommendation/recommendProducts',
  async (request, { getState, dispatch }) => {
    const form = getState().solarRecommendation.form;
    const powerNeeded = calculateTotalPowerNeeded(form.appliances);
    const monthsToPay = getMonthsToPay(form);
    const monthlyBudget = getMonthlyBudget(form);

    try {
      // Create the parameters for each endpoint
      const bestFitParams = {
        powerNeeded,
        monthsToPay,
        monthlyBudget
      };

      const budgetFitParams = {
        page: request.page,
        pageSize: request.pageSize,
        monthsToPay,
        monthlyBudget
      };

      const powerFitParams = {
        page: request.page,
        pageSize: request.pageSize,
        powerNeeded
      };

      // Use the endpoints from the service directly
      const bestFitPromise = dispatch(
        service.solarProductEndpoints.endpoints.recommendBestFit.initiate(bestFitParams)
      );
      
      const budgetFitPromise = dispatch(
        service.solarProductEndpoints.endpoints.recommendBudgetFit.initiate(budgetFitParams)
      );
      
      const powerNeedsFitPromise = dispatch(
        service.solarProductEndpoints.endpoints.recommendPowerFit.initiate(powerFitParams)
      );

      // Wait for all promises to resolve
      const [bestFitResult, budgetFitResult, powerNeedsFitResult] = await Promise.all([
        bestFitPromise,
        budgetFitPromise,
        powerNeedsFitPromise
      ]);

      // Check for errors and throw if needed
      if (bestFitResult.error) throw new Error(JSON.stringify(bestFitResult.error));
      if (budgetFitResult.error) throw new Error(JSON.stringify(budgetFitResult.error));
      if (powerNeedsFitResult.error) throw new Error(JSON.stringify(powerNeedsFitResult.error));

      return {
        bestFit: bestFitResult.data!,
        budgetFit: budgetFitResult.data!,
        powerNeedsFit: powerNeedsFitResult.data!,
      };
    } catch (error: any) {
      throw new Error(error.message || "Something went wrong. Please try again later.");
    }
  },
);

export const recommendBestFitProducts = createAsyncThunk<SolarProduct[], void, { state: RootState }>(
  'solarRecommendation/recommendBestFitProducts',
  async (_, { getState, dispatch }) => {
    const form = getState().solarRecommendation.form;
    const powerNeeded = calculateTotalPowerNeeded(form.appliances);
    const monthsToPay = getMonthsToPay(form);
    const monthlyBudget = getMonthlyBudget(form);

    try {
      const params = {
        powerNeeded,
        monthsToPay,
        monthlyBudget
      };

      const result = await dispatch(
        service.solarProductEndpoints.endpoints.recommendBestFit.initiate(params)
      );

      if (result.error) throw new Error(JSON.stringify(result.error));
      return result.data!;
    } catch (error: any) {
      throw new Error(error.message || "Something went wrong. Please try again later.");
    }
  },
);

export const recommendBudgetFitProducts = createAsyncThunk<PaginatedList<SolarProduct>, { page: number, pageSize: number }, { state: RootState }>(
  'solarRecommendation/recommendBudgetFitProducts',
  async (request, { getState, dispatch }) => {
    const form = getState().solarRecommendation.form;
    const monthsToPay = getMonthsToPay(form);
    const monthlyBudget = getMonthlyBudget(form);

    try {
      const params = {
        page: request.page,
        pageSize: request.pageSize,
        monthsToPay,
        monthlyBudget
      };

      const result = await dispatch(
        service.solarProductEndpoints.endpoints.recommendBudgetFit.initiate(params)
      );

      if (result.error) throw new Error(JSON.stringify(result.error));
      return result.data!;
    } catch (error: any) {
      throw new Error(error.message || "Something went wrong. Please try again later.");
    }
  },
);

export const recommendPowerNeedsFitProducts = createAsyncThunk<PaginatedList<SolarProduct>, { page: number, pageSize: number }, { state: RootState }>(
  'solarRecommendation/recommendPowerNeedsFitProducts',
  async (request, { getState, dispatch }) => {
    const form = getState().solarRecommendation.form;
    const powerNeeded = calculateTotalPowerNeeded(form.appliances);

    try {
      const params = {
        page: request.page,
        pageSize: request.pageSize,
        powerNeeded
      };

      const result = await dispatch(
        service.solarProductEndpoints.endpoints.recommendPowerFit.initiate(params)
      );

      if (result.error) throw new Error(JSON.stringify(result.error));
      return result.data!;
    } catch (error: any) {
      throw new Error(error.message || "Something went wrong. Please try again later.");
    }
  },
);